import type { IChatMessage, IChatProcessingStep } from '@/apiClient/types/chat'

/**
 * Web pages the backend's HTML agent builds. When the agent is switched on server-side, a
 * turn about a page gets the `create_html_page` / `edit_html_page` tools; the call starts a
 * background job instead of the model writing the HTML. `message_complete` names the job
 * (`background_job_type: 'html'`), websocket progress streams steps into `ui_hints`, and on
 * success the server rewrites the reply's content with the page as an ```html block.
 *
 * Unlike `FILE_GENERATION_JOBS` there is no composer mode and no file: nothing on the
 * request asks for it, and the page lands in the reply's text.
 */
export const HTML_AGENT_JOB_TYPE = 'html'

/** The tools whose call starts the job; the last one's row carries its progress. */
export const HTML_AGENT_TOOL_NAMES = ['create_html_page', 'edit_html_page']

/** `ui_hints.processing_steps[].step` the job's progress is saved under. */
export const HTML_AGENT_PROGRESS_STEP = 'html_generation'

/** Title of the progress row while running, before the job reports its own. */
export const HTML_AGENT_PROGRESS_TITLE = 'Building the page'

export const HTML_AGENT_FAILURE_MESSAGE = 'Page generation failed'
export const HTML_AGENT_CANCELLED_MESSAGE = 'Page generation stopped'
export const HTML_AGENT_STALLED_MESSAGE =
  'Page generation is taking longer than expected'

export type HtmlAgentJobEventType =
  | 'html_job_progress'
  | 'html_generation_job_succeeded'
  | 'html_generation_job_failed'
  | 'html_generation_job_cancelled'

/** One tool call the agent made while building the page, as the job saves it on its step. */
export interface IHtmlAgentAction {
  /** The call's id; a call's later update replaces its entry. */
  id: string
  /** The agent's tool, e.g. `create_html`, `list_official_assets`. */
  tool: string
  /** The API's label, for a tool the client has no copy for. */
  label: string
  status: 'running' | 'succeeded' | 'failed'
  /** An edit's changes, one line each, e.g. `Changed "Banana" to "Orange"`. */
  changes?: string[]
  /** Why a failed call failed, as the agent reported it. */
  error?: string
  /**
   * How much of the agent's reasoning (`htmlAgentReasoning`) was written when the call began,
   * to order its thoughts against its actions. Absent on pages built before it was saved.
   */
  reasoning_offset?: number
}

const isHtmlAgentAction = (value: unknown): value is IHtmlAgentAction => {
  if (!value || typeof value !== 'object') return false
  const action = value as Record<string, unknown>
  return (
    typeof action.id === 'string' &&
    typeof action.tool === 'string' &&
    typeof action.label === 'string' &&
    (action.status === 'running' ||
      action.status === 'succeeded' ||
      action.status === 'failed') &&
    (action.changes === undefined ||
      (Array.isArray(action.changes) &&
        action.changes.every((change) => typeof change === 'string'))) &&
    (action.error === undefined || typeof action.error === 'string') &&
    (action.reasoning_offset === undefined ||
      typeof action.reasoning_offset === 'number')
  )
}

/** The agent's actions saved on the job's progress step (`data.actions`), in order. */
export const htmlAgentActions = (
  step: IChatProcessingStep,
): IHtmlAgentAction[] => {
  const actions = step.data.actions
  return Array.isArray(actions) ? actions.filter(isHtmlAgentAction) : []
}

/**
 * The agent's reasoning summary saved on the job's progress step (`data.reasoning`): titled
 * thoughts, the same shape as a reply's `reasoning_content`. Saved with each action and when
 * the job ends; the websocket's live copy runs ahead of it while the job runs.
 */
export const htmlAgentReasoning = (step: IChatProcessingStep): string => {
  const reasoning = step.data.reasoning
  return typeof reasoning === 'string' ? reasoning : ''
}

const TITLE_ELEMENT = /<title[^>]*>([\s\S]*?)<\/title>/i

/** A draft's `<title>`, for its card. Found without parsing the whole page, once a second. */
export const htmlDraftTitle = (html: string) => {
  const title = TITLE_ELEMENT.exec(html)?.[1]
  if (!title) return ''
  // Only the title's own text is parsed, for its entities (`&amp;`…).
  return (
    new DOMParser()
      .parseFromString(title, 'text/html')
      .documentElement.textContent?.trim() ?? ''
  )
}

/** The reply's page job while it runs (this session, or after a reload). */
export const runningHtmlAgentJob = (message: IChatMessage) =>
  message.pending_jobs?.find(
    (job) =>
      job.job_type === HTML_AGENT_JOB_TYPE &&
      (job.status === 'running' || job.status === 'pending'),
  )

/** The reply's page is still being built (this session, or after a reload). */
export const isHtmlAgentJobRunning = (message: IChatMessage) =>
  !!runningHtmlAgentJob(message)
